import type { QrCorrection } from "../../common/qr";
import { type MailFields, type SmsFields, writeCall, writeLink, writeMail, writeSms } from "./links";
import { type VCardFields, writeVCard } from "./vcard";
import { type WifiFields, writeWifi } from "./wifi";

export type Kind = "text" | "url" | "wifi" | "vcard" | "email" | "phone" | "sms";

// The label is the picker's, and the hint is what the result card says while the field it is waiting on is blank: a
// page that encodes as you type has no call to action to hold a `Required` until, so it says what it wants instead.
export const KINDS: Record<Kind, KindSpec> = {
  text: { label: "Plain Text", hint: "Type something and its code appears here." },
  url: { label: "URL", hint: "Enter a web address and its code appears here." },
  wifi: { label: "WiFi", hint: "Enter the network name and its code appears here." },
  vcard: { label: "vCard", hint: "Enter a name or an organisation and the card's code appears here." },
  email: { label: "Email", hint: "Enter an address and its code appears here." },
  phone: { label: "Phone Call", hint: "Enter a number and its code appears here." },
  sms: { label: "SMS", hint: "Enter a number and its code appears here." },
};

export interface KindSpec {
  label: string;
  hint: string;
}

// The picker reads the table rather than a second list of the same words, so the order on screen is the order above.
export const KIND_OPTIONS = (Object.keys(KINDS) as Kind[]).map((kind) => ({ value: kind, label: KINDS[kind].label }));

// The redundancy a code carries, which is the share of it a reader can lose and still get the text back. Low fits
// the most in and High survives a code somebody printed on a mug, so the choice is the reader's rather than a default
// nobody was asked about.
export const CORRECTION_OPTIONS = [
  { value: "L", label: "Low (7%)" },
  { value: "M", label: "Medium (15%)" },
  { value: "Q", label: "Quartile (25%)" },
  { value: "H", label: "High (30%)" },
];

export interface Fields {
  text: string;
  url: string;
  wifi: WifiFields;
  vcard: VCardFields;
  mail: MailFields;
  phone: string;
  sms: SmsFields;
}

// The one thing a camera is handed, which is also the one thing the box under the code shows. Empty is a kind whose
// fields are not filled in yet, and is the card's hint rather than a code of nothing.
export function writePayload(kind: Kind, fields: Fields): string {
  switch (kind) {
    case "text":
      // As typed rather than trimmed, a newline at the end of a note being somebody's and not the page's to take.
      return fields.text.trim() ? fields.text : "";
    case "url":
      return writeLink(fields.url);
    case "wifi":
      return writeWifi(fields.wifi);
    case "vcard":
      return writeVCard(fields.vcard);
    case "email":
      return writeMail(fields.mail);
    case "phone":
      return writeCall(fields.phone);
    case "sms":
      return writeSms(fields.sms);
  }
}

export function pickKind(value: unknown): Kind {
  return typeof value === "string" && value in KINDS ? value as Kind : "text";
}

export function pickCorrection(value: unknown): QrCorrection {
  return value === "L" || value === "Q" || value === "H" ? value : "M";
}

export function pickText(value: unknown): string {
  return typeof value === "string" ? value : "";
}
